import React, { useEffect, useRef } from 'react';

// A tiny stick figure that lags behind and runs to catch up with the cursor.
const CursorRunner = () => {
  const wrap = useRef(null);
  const fig = useRef(null);
  const legA = useRef(null);
  const legB = useRef(null);
  const armA = useRef(null);
  const armB = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const coarse = window.matchMedia('(pointer: coarse)').matches;
    if (reduce || coarse) return;

    let tx = window.innerWidth / 2;
    let ty = window.innerHeight / 2;
    let x = tx;
    let y = ty;
    let phase = 0;
    let facing = 1;
    let visible = false;
    let raf = 0;

    const HIP = -13;
    const SHO = -24;

    const show = () => {
      if (!visible && wrap.current) {
        wrap.current.style.opacity = '1';
        visible = true;
      }
    };
    const hide = () => {
      if (wrap.current) wrap.current.style.opacity = '0';
      visible = false;
    };

    const onMove = (e) => {
      tx = e.clientX;
      ty = e.clientY;
      show();
    };
    const onOut = (e) => {
      if (!e.relatedTarget) hide();
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseout', onOut);

    const tick = () => {
      const dx = tx - x;
      const dy = ty - y;
      const dist = Math.hypot(dx, dy);
      x += dx * 0.1;
      y += dy * 0.1;

      const speed = Math.min(dist, 80);
      if (Math.abs(dx) > 0.4) facing = dx < 0 ? -1 : 1;

      phase += 0.12 + speed * 0.012;
      const amp = 12 + Math.min(speed * 0.55, 28);
      const swingA = Math.sin(phase) * amp;
      const swingB = Math.sin(phase + Math.PI) * amp;
      const bob = Math.abs(Math.cos(phase)) * (1 + Math.min(speed * 0.05, 3));

      if (wrap.current) wrap.current.style.transform = `translate3d(${x}px, ${y}px, 0)`;
      if (fig.current)
        fig.current.style.transform = `translate(-50%, -100%) translateY(${-bob}px) scaleX(${facing})`;
      if (legA.current) legA.current.setAttribute('transform', `rotate(${swingA} 0 ${HIP})`);
      if (legB.current) legB.current.setAttribute('transform', `rotate(${swingB} 0 ${HIP})`);
      if (armA.current) armA.current.setAttribute('transform', `rotate(${swingB} 0 ${SHO})`);
      if (armB.current) armB.current.setAttribute('transform', `rotate(${swingA} 0 ${SHO})`);

      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseout', onOut);
    };
  }, []);

  return (
    <div
      ref={wrap}
      aria-hidden="true"
      style={{
        position: 'fixed',
        left: 0,
        top: 0,
        zIndex: 9998,
        pointerEvents: 'none',
        opacity: 0,
        transition: 'opacity 0.3s ease',
        willChange: 'transform',
      }}
    >
      <svg
        ref={fig}
        width="34"
        height="46"
        viewBox="-14 -34 28 38"
        fill="none"
        stroke="#004225"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ willChange: 'transform' }}
      >
        <circle cx="0" cy="-28" r="3.4" fill="#004225" stroke="none" />
        <line x1="0" y1="-24.5" x2="0" y2="-13" />
        <g ref={armA}>
          <line x1="0" y1="-24" x2="6" y2="-17" />
        </g>
        <g ref={armB}>
          <line x1="0" y1="-24" x2="-5" y2="-17" />
        </g>
        <g ref={legA}>
          <line x1="0" y1="-13" x2="5" y2="-1" />
        </g>
        <g ref={legB}>
          <line x1="0" y1="-13" x2="-5" y2="-1" />
        </g>
      </svg>
    </div>
  );
};

export default CursorRunner;
